type BaseEventOptions = {
  referrer?: string
  google_id?: string
}

type ViewPageOptions = BaseEventOptions & {
  experiment_group?: string
  policyNumber?: string
  quoteNumber?: string
  quoteType?: string
  price?: number | string
  params?: Record<string, string | undefined>
}

type FormSubmitOptions = BaseEventOptions & {
  params?: Record<string, string | undefined>
}

const PUSH_EVENT_PATH = '/events/?action=pushevent'
const QIC_PRODUCT_CODE = 'visitors'
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
const GA_CLIENT_ID_PREFIX = /^GA\d+\.\d+\./

const stripGaPrefix = (clientId: string) => clientId.replace(GA_CLIENT_ID_PREFIX, '')

const resolveGoogleId = (override?: string) => {
  if (override !== undefined) return stripGaPrefix(override)
  return window.gaGlobal?.vid ?? ''
}

const setIfDefined = (body: URLSearchParams, key: string, value: string | null | undefined) => {
  if (value) body.set(key, value)
}

const buildBaseBody = ({
  event_name,
  referrer,
  google_id,
}: BaseEventOptions & { event_name: string }) => {
  const url = new URL(window.location.href)

  const body = new URLSearchParams({
    event_name,
    path: url.pathname,
    url: url.href,
    referrer: referrer ?? document.referrer,
    google_id: resolveGoogleId(google_id),
    timestamp: Math.floor(Date.now() / 1000).toString(),
  })

  return { body, url }
}

const sendEvent = (body: URLSearchParams) =>
  fetch(`${import.meta.env.VUE_APP_PUBLIC_API_URL}${PUSH_EVENT_PATH}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Qic-Product-Code': QIC_PRODUCT_CODE,
    },
    body: body.toString(),
  })

export const PushEventService = {
  viewPage({
    referrer,
    google_id,
    experiment_group,
    policyNumber,
    quoteNumber,
    quoteType,
    price,
    params,
  }: ViewPageOptions = {}) {
    const { body, url } = buildBaseBody({ event_name: 'view_page', referrer, google_id })

    for (const key of UTM_KEYS) {
      setIfDefined(body, key, url.searchParams.get(key))
    }

    setIfDefined(body, 'experiment_group', experiment_group)
    setIfDefined(body, 'policyNumber', policyNumber)
    setIfDefined(body, 'quoteNumber', quoteNumber)
    setIfDefined(body, 'quoteType', quoteType)
    setIfDefined(body, 'price', price?.toString())
    setIfDefined(body, 'params', params && JSON.stringify(params))

    return sendEvent(body)
  },

  formSubmit({ referrer, google_id, params }: FormSubmitOptions = {}) {
    const { body } = buildBaseBody({ event_name: 'form_submit', referrer, google_id })

    setIfDefined(body, 'params', params && JSON.stringify(params))

    return sendEvent(body)
  },
}
